// src/components/ProductManagement/ProductManagement.jsx
import React, { useState, useEffect } from 'react';
import productService from '../../services/productService';
import ProductTable from './ProductTable';
import ProductAddModal from './ProductAddModal';
import ProductEditModal from './ProductEditModal';
import { showNotification } from '../../utils/notification';
import './ProductManagement.css';
import { PlusCircle } from 'lucide-react';


const ProductManagement = ({ toggleSidebar }) => {
  const [allProducts, setAllProducts] = useState([]); // Store all products
  const [filteredProducts, setFilteredProducts] = useState([]); // Store filtered products
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const [filters, setFilters] = useState({
    search: '',
    category: ''
  });

  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Load all data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Auto-filter whenever filters change
  useEffect(() => {
    applyFilters();
  }, [filters, allProducts]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      const [categoriesData, productsData] = await Promise.all([
        productService.getActiveCategories(),
        productService.getProducts(1, 10000) // Load all products
      ]);
      
      setCategories(categoriesData);
      setAllProducts(productsData);
      showNotification(`Loaded ${productsData.length} products`, 'success');
    } catch (error) {
      showNotification(`Failed to load: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Client-side filtering (real-time)
  const applyFilters = () => {
    let filtered = [...allProducts];

    // Filter by search text
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter(product => 
        (product.name && product.name.toLowerCase().includes(searchLower)) ||
        (product.sku && product.sku.toLowerCase().includes(searchLower)) ||
        (product.description && product.description.toLowerCase().includes(searchLower))
      );
    }

    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(product => 
        product.category?.id == filters.category || product.categoryId == filters.category
      );
    }

    setFilteredProducts(filtered);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: ''
    });
    showNotification('Filters cleared', 'info');
  };

  // ========== CLIENT-SIDE PAGINATION ==========
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) pages.push(i);
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
      }
    }
    
    return pages;
  };

  const handleAddProductsSave = async (payload) => {
    try {
      setIsSaving(true);
      await productService.addProducts(payload);
      showNotification('Products added successfully!', 'success');
      setIsAddModalOpen(false);
      await loadData();
    } catch (error) {
      showNotification(`Failed: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setIsEditModalOpen(true);
  };

  const handleEditSave = async (productId, payload) => {
    try {
      setIsSaving(true);
      await productService.updateProduct(productId, payload);
      showNotification('Product updated successfully!', 'success');
      setIsEditModalOpen(false);
      setSelectedProduct(null);
      await loadData();
    } catch (error) {
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await productService.deleteProduct(productId);
      showNotification('Product deleted successfully!', 'success');
      await loadData();
    } catch (error) {
      showNotification(`Failed to delete: ${error.message}`, 'error');
    }
  };

  return (
    <main className="inventory-main-content">
      <header className="content-header">
        <button className="btn-toggle-sidebar" onClick={toggleSidebar}>☰</button>
        <h2 className="content-title">Product Management</h2>
        <div className="header-actions">
          <button 
            className="btn-primary2" 
            onClick={() => setIsAddModalOpen(true)}
          > <PlusCircle size={18} />
            Add Products
          </button>
        </div>
      </header>

      <section className="filter-controls">
        <div className="filter-group">
          <input 
            type="text" 
            className="custom-input search-input" 
            placeholder="Search by name, SKU, or description..."
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            style={{ flex: 1, minWidth: '300px' }}
          />

          <select
            className="custom-select"
            name="category"
            value={filters.category}
            onChange={handleFilterChange}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          
          <button 
            className="inventory-btn inventory-btn-clear" 
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        </div>

        {/* Filter Summary with Items Per Page */}
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'left' }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Showing {filteredProducts.length > 0 ? indexOfFirstItem + 1 : 0}-{Math.min(indexOfLastItem, filteredProducts.length)} of <strong>{filteredProducts.length}</strong> filtered products (Total: <strong>{allProducts.length}</strong>)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: '#666' }}>Items per page:</label>
            <select 
              className="custom-select" 
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{ width: '80px' }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
        </div>
      </section>

      <ProductTable
        products={currentProducts}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Enhanced Pagination */}
      {filteredProducts.length > 0 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: '8px',
          marginTop: '24px',
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          {/* Previous Button */}
          <button 
            className="btn" 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(currentPage - 1)}
            title="Previous Page"
            style={{ minWidth: '80px' }}
          >
            ‹ Prev
          </button>
          
          {/* Page Numbers */}
          <div style={{ display: 'flex', gap: '4px', margin: '0 12px' }}>
            {getPageNumbers().map((pageNum) => (
              <button
                key={pageNum}
                className={`btn ${currentPage === pageNum ? 'btn-page' : ''}`}
                onClick={() => handlePageChange(pageNum)}
                style={{ 
                  minWidth: '40px',
                  fontWeight: currentPage === pageNum ? 'bold' : 'normal'
                }}
              >
                {pageNum}
              </button>
            ))}
          </div>

          {/* Page Info */}
          <div style={{ 
            marginLeft: '16px', 
            padding: '8px 16px',
            backgroundColor: 'white',
            borderRadius: '4px',
            border: '1px solid #dee2e6',
            fontSize: '14px',
            color: '#495057',
            fontWeight: '500'
          }}>
            Page {currentPage} of {totalPages}
          </div>
          
          {/* Next Button */}
          <button 
            className="btn btn-page" 
            disabled={currentPage === totalPages} 
            onClick={() => handlePageChange(currentPage + 1)}
            title="Next Page"
            style={{ minWidth: '80px' }}
          >
            Next ›
          </button>
        </div>
      )}

      {/* No Results Message */}
      {filteredProducts.length === 0 && !isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#6c757d',
          fontSize: '16px'
        }}>
          No products found matching your filters. Try adjusting your search criteria.
        </div>
      )}

      <ProductAddModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddProductsSave}
        isSaving={isSaving}
        categories={categories}
      />

      <ProductEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProduct(null);
        }}
        onSave={handleEditSave}
        isSaving={isSaving}
        categories={categories}
        product={selectedProduct}
      />
    </main>
  );
};

export default ProductManagement;